import React, { useState } from 'react';
import {
  IoConstruct,
  IoCreate,
  IoHeart,
  IoInformationCircle,
  IoStatsChart,
  IoDocumentText,
  IoMedal,
  IoFlask,
  IoHeartOutline,
  IoMusicalNotes,
  IoNewspaper,
  IoBulb,
  IoCalendar,
  IoSchool,
  IoTime,
  IoEye,
  IoDownload,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoArrowForward,
  IoEllipsisVertical,
  IoFileTray,
  IoAdd,
} from 'react-icons/io5';
import InfoButton from '@/components/InfoButton';
import './Applications.css';

interface CollegeData {
  name: string;
  deadline: string;
  status: string;
  statusColor: string;
  daysLeft: string;
  daysLeftColor: string;
  iconColor: string;
  iconTextColor: string;
  progress: number;
  interestStatus: string;
  interestStatusColor: string;
  interestDemonstrated: boolean;
  interestInfo: string;
  interestInfoColor: string;
  essayTitle: string;
}

const Applications: React.FC = () => {
  const [selectedCollege, setSelectedCollege] = useState<string>('stanford');

  const collegeData: Record<string, CollegeData> = {
    stanford: {
      name: 'Stanford University',
      deadline: 'Regular Decision • Deadline: Jan 5, 2025',
      status: 'In Progress',
      statusColor: 'bg-yellow-100 text-yellow-700',
      daysLeft: '7 days left',
      daysLeftColor: 'bg-red-100 text-red-700',
      iconColor: 'bg-red-100',
      iconTextColor: 'text-red-600',
      progress: 85,
      interestStatus: 'Strongly Considered',
      interestStatusColor: 'bg-green-100 text-green-700',
      interestDemonstrated: true,
      interestInfo: "You've demonstrated strong interest through campus visits, interviews, and engagement with admissions materials.",
      interestInfoColor: 'bg-green-50 border-green-200',
      essayTitle: 'Stanford Supplemental Essay',
    },
    harvard: {
      name: 'Harvard University',
      deadline: 'Regular Decision • Deadline: Jan 10, 2025',
      status: 'In Progress',
      statusColor: 'bg-yellow-100 text-yellow-700',
      daysLeft: '12 days left',
      daysLeftColor: 'bg-yellow-100 text-yellow-700',
      iconColor: 'bg-blue-100',
      iconTextColor: 'text-blue-600',
      progress: 70,
      interestStatus: 'Somewhat Considered',
      interestStatusColor: 'bg-yellow-100 text-yellow-700',
      interestDemonstrated: true,
      interestInfo: "You've shown some interest through application submission. Consider attending virtual events or connecting with admissions.",
      interestInfoColor: 'bg-yellow-50 border-yellow-200',
      essayTitle: 'Harvard Supplemental Essays',
    },
    mit: {
      name: 'MIT',
      deadline: 'Early Action • Deadline: Jan 15, 2025',
      status: 'In Progress',
      statusColor: 'bg-yellow-100 text-yellow-700',
      daysLeft: '17 days left',
      daysLeftColor: 'bg-red-100 text-red-700',
      iconColor: 'bg-purple-100',
      iconTextColor: 'text-purple-600',
      progress: 75,
      interestStatus: 'Not Considered',
      interestStatusColor: 'bg-gray-100 text-gray-700',
      interestDemonstrated: false,
      interestInfo: 'MIT does not consider demonstrated interest in admissions decisions. Focus on showcasing your academic achievements and passion for STEM.',
      interestInfoColor: 'bg-gray-50 border-gray-200',
      essayTitle: 'MIT "World You Come From" Essay',
    },
    yale: {
      name: 'Yale University',
      deadline: 'Regular Decision • Deadline: Jan 2, 2025',
      status: 'Submitted',
      statusColor: 'bg-green-100 text-green-700',
      daysLeft: 'Submitted',
      daysLeftColor: 'bg-green-100 text-green-700',
      iconColor: 'bg-blue-100',
      iconTextColor: 'text-blue-600',
      progress: 100,
      interestStatus: 'Strongly Considered',
      interestStatusColor: 'bg-green-100 text-green-700',
      interestDemonstrated: true,
      interestInfo: "You've demonstrated strong interest through campus visits, interviews, and engagement with admissions materials.",
      interestInfoColor: 'bg-green-50 border-green-200',
      essayTitle: 'Yale Supplemental Essays',
    },
    princeton: {
      name: 'Princeton University',
      deadline: 'Regular Decision • Deadline: Jan 1, 2025',
      status: 'Under Review',
      statusColor: 'bg-blue-100 text-blue-700',
      daysLeft: 'Under Review',
      daysLeftColor: 'bg-blue-100 text-blue-700',
      iconColor: 'bg-purple-100',
      iconTextColor: 'text-purple-600',
      progress: 100,
      interestStatus: 'Somewhat Considered',
      interestStatusColor: 'bg-yellow-100 text-yellow-700',
      interestDemonstrated: true,
      interestInfo: "You've shown some interest through application submission. Consider attending virtual events or connecting with admissions.",
      interestInfoColor: 'bg-yellow-50 border-yellow-200',
      essayTitle: 'Princeton Supplemental Essays',
    },
  };

  const currentCollege = collegeData[selectedCollege] || collegeData.stanford;

  const getInterestIconColor = (colorClass: string) => {
    if (colorClass.includes('green')) return 'text-green-600';
    if (colorClass.includes('yellow')) return 'text-yellow-600';
    return 'text-gray-600';
  };

  return (
    <div className="applications-page">
      <div className="applications-container">
        <header className="applications-header">
          <div className="header-content">
            <h1 className="applications-title">My Applications</h1>
            <div className="selector-group">
              <label className="selector-label">
                Select Application:
                <InfoButton elementId="applications-college-selector" />
              </label>
              <select
                className="college-selector"
                value={selectedCollege}
                onChange={(e) => setSelectedCollege(e.target.value)}
              >
                <option value="all">All Applications</option>
                <option value="stanford">Stanford University</option>
                <option value="harvard">Harvard University</option>
                <option value="mit">MIT</option>
                <option value="yale">Yale University</option>
                <option value="princeton">Princeton University</option>
              </select>
            </div>
          </div>
        </header>

        <div className="application-preview">
          {/* Application Header */}
          <div className="application-header-card">
            <div className="header-top">
              <div className="college-info">
                <div className={`college-icon ${currentCollege.iconColor}`}>
                  <IoDocumentText className={`${currentCollege.iconTextColor} text-2xl`} />
                </div>
                <div>
                  <h2 className="college-name">{currentCollege.name}</h2>
                  <p className="college-deadline">{currentCollege.deadline}</p>
                  <div className="status-badges">
                    <span className={`status-badge ${currentCollege.statusColor}`}>
                      {currentCollege.status}
                    </span>
                    <span className={`days-left-badge ${currentCollege.daysLeftColor}`}>
                      {currentCollege.daysLeft}
                    </span>
                  </div>
                </div>
              </div>
              <div className="header-actions">
                <button className="btn-primary">
                  <IoConstruct className="mr-2" />
                  <span>AI Review</span>
                </button>
                <button className="btn-secondary">
                  <IoCreate className="mr-2" />
                  <span>Edit</span>
                </button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-section">
              <div className="progress-header">
                <span className="progress-label">Overall Progress</span>
                <span className="progress-percentage">{currentCollege.progress}%</span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${currentCollege.progress}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Demonstrated Interest Section */}
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">
                Demonstrated Interest
                <InfoButton elementId="applications-demonstrated-interest" />
              </h2>
              <IoHeart className="text-primary text-xl" />
            </div>
            <div className="interest-content">
              <div className="interest-info-group">
                <div className="interest-item">
                  <span className="interest-label">Status:</span>
                  <span className={`interest-status-badge ${currentCollege.interestStatusColor}`}>
                    {currentCollege.interestStatus}
                  </span>
                </div>
                <div className="interest-item">
                  <span className="interest-label">Demonstrated:</span>
                  {currentCollege.interestDemonstrated ? (
                    <>
                      <IoCheckmarkCircle className="text-green-600 text-lg" />
                      <span className="interest-value">Yes</span>
                    </>
                  ) : (
                    <>
                      <IoCloseCircle className="text-red-600 text-lg" />
                      <span className="interest-value">No</span>
                    </>
                  )}
                </div>
              </div>
              <a href="#" className="view-opportunities-link">
                <span>View opportunities</span>
                <IoArrowForward className="text-xs" />
              </a>
            </div>
            <div className={`interest-info-box ${currentCollege.interestInfoColor}`}>
              <p className="interest-info-text">
                <IoInformationCircle className={`${getInterestIconColor(currentCollege.interestInfoColor)} mr-1`} />
                {currentCollege.interestInfo}
              </p>
            </div>
          </div>

          {/* Academic Profile Section */}
          <div className="section-card">
            <h2 className="section-title mb-6">
              Academic Profile
              <InfoButton elementId="applications-academic-profile" />
            </h2>

            <div className="academic-grid">
              {/* GPA Card */}
              <div className="academic-card">
                <div className="academic-card-header">
                  <h3 className="academic-card-title">GPA Overview</h3>
                  <IoStatsChart className="text-primary" />
                </div>
                <div className="academic-card-content">
                  <div className="gpa-item">
                    <div className="gpa-header">
                      <span className="gpa-label">Unweighted GPA</span>
                      <span className="gpa-value">3.92</span>
                    </div>
                    <div className="progress-bar-small">
                      <div className="progress-fill-primary" style={{ width: '98%' }}></div>
                    </div>
                  </div>
                  <div className="gpa-item">
                    <div className="gpa-header">
                      <span className="gpa-label">Weighted GPA</span>
                      <span className="gpa-value">4.35</span>
                    </div>
                    <div className="progress-bar-small">
                      <div className="progress-fill-green" style={{ width: '87%' }}></div>
                    </div>
                  </div>
                  <div className="gpa-stats">
                    <div className="stat-row">
                      <span className="stat-label">Class Rank</span>
                      <span className="stat-value">12 / 487</span>
                    </div>
                    <div className="stat-row">
                      <span className="stat-label">Percentile</span>
                      <span className="stat-value-green">Top 3%</span>
                    </div>
                  </div>
                </div>
                <div className="info-box-blue">
                  <div className="info-box-header">
                    <div className="info-box-content">
                      <IoInformationCircle className="text-blue-600" />
                      <p className="info-text">
                        <span className="info-bold">GPA:</span> <span className="info-normal">Required</span> |{' '}
                        <span className="info-bold">Average GPA:</span> <span className="info-normal">3.75</span>
                      </p>
                    </div>
                  </div>
                  <p className="percentile-text">
                    <IoStatsChart />
                    You are in the <span className="percentile-value">75th percentile</span>
                  </p>
                </div>
              </div>

              {/* Test Scores Card */}
              <div className="academic-card">
                <div className="academic-card-header">
                  <h3 className="academic-card-title">Test Scores</h3>
                  <IoDocumentText className="text-primary" />
                </div>
                <div className="test-scores-content">
                  <div className="test-score-item">
                    <div className="test-score-header">
                      <span className="test-score-label">SAT</span>
                      <span className="test-score-value">1480</span>
                    </div>
                    <div className="test-score-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">Math</span>
                        <p className="breakdown-value">750</p>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Reading</span>
                        <p className="breakdown-value">730</p>
                      </div>
                    </div>
                  </div>
                  <div className="test-score-divider"></div>
                  <div className="test-score-item">
                    <div className="test-score-header">
                      <span className="test-score-label">ACT</span>
                      <span className="test-score-value">33</span>
                    </div>
                    <div className="test-score-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">English</span>
                        <p className="breakdown-value">34</p>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Math</span>
                        <p className="breakdown-value">35</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="info-box-purple">
                  <div className="info-box-header">
                    <div className="info-box-content">
                      <IoInformationCircle className="text-purple-600" />
                      <p className="info-text">
                        <span className="info-bold">SAT/ACT:</span> <span className="info-normal">Strongly Recommended</span> |{' '}
                        <span className="info-bold">Average SAT:</span> <span className="info-normal">1400</span> |{' '}
                        <span className="info-bold">Average ACT:</span> <span className="info-normal">33</span>
                      </p>
                    </div>
                  </div>
                  <p className="percentile-text">
                    <IoStatsChart />
                    You are in the <span className="percentile-value">75th percentile</span>
                  </p>
                </div>
              </div>

              {/* AP Scores Card */}
              <div className="academic-card">
                <div className="academic-card-header">
                  <h3 className="academic-card-title">AP Scores</h3>
                  <IoMedal className="text-primary" />
                </div>
                <div className="ap-scores-list">
                  <div className="ap-score-item">
                    <span className="ap-score-name">Calculus BC</span>
                    <span className="ap-score-badge-green">5</span>
                  </div>
                  <div className="ap-score-item">
                    <span className="ap-score-name">Chemistry</span>
                    <span className="ap-score-badge-green">5</span>
                  </div>
                  <div className="ap-score-item">
                    <span className="ap-score-name">US History</span>
                    <span className="ap-score-badge-blue">4</span>
                  </div>
                  <div className="ap-score-item">
                    <span className="ap-score-name">English Lang</span>
                    <span className="ap-score-badge-green">5</span>
                  </div>
                  <div className="ap-score-item-last">
                    <span className="ap-score-name">Physics C</span>
                    <span className="ap-score-badge-blue">4</span>
                  </div>
                </div>
                <div className="ap-average">
                  <p className="ap-average-text">
                    Average Score: <span className="ap-average-value">4.6</span>
                  </p>
                </div>
                <div className="info-box-amber">
                  <div className="info-box-header">
                    <div className="info-box-content">
                      <IoInformationCircle className="text-amber-600" />
                      <p className="info-text">
                        <span className="info-bold">AP Scores:</span> <span className="info-normal">Recommended</span> |{' '}
                        <span className="info-bold">Average Score:</span> <span className="info-normal">4.2</span>
                      </p>
                    </div>
                  </div>
                  <p className="percentile-text">
                    <IoStatsChart />
                    You are in the <span className="percentile-value">75th percentile</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Activities Section */}
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">
                Extracurricular Activities
                <InfoButton elementId="applications-activities" />
              </h2>
              <button className="section-action-btn">
                <IoAdd className="mr-1" />
                Add Activity
              </button>
            </div>

            <div className="activities-grid">
              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-info">
                    <div className="activity-icon-blue">
                      <IoFlask className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="activity-title">Science Olympiad</h3>
                      <p className="activity-subtitle">Team Captain • 4 years</p>
                    </div>
                  </div>
                  <button className="activity-menu-btn">
                    <IoEllipsisVertical />
                  </button>
                </div>
                <p className="activity-description">
                  Led team of 15 students to regional championships. Competed in Chemistry Lab and Disease Detectives events.
                </p>
                <div className="activity-tags">
                  <span className="tag-blue">Leadership</span>
                  <span className="tag-purple">STEM</span>
                </div>
              </div>

              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-info">
                    <div className="activity-icon-green">
                      <IoHeartOutline className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="activity-title">Community Volunteer</h3>
                      <p className="activity-subtitle">Volunteer Coordinator • 3 years</p>
                    </div>
                  </div>
                  <button className="activity-menu-btn">
                    <IoEllipsisVertical />
                  </button>
                </div>
                <p className="activity-description">
                  Organized weekly tutoring sessions for underprivileged students. Accumulated 300+ volunteer hours.
                </p>
                <div className="activity-tags">
                  <span className="tag-green">Community Service</span>
                  <span className="tag-blue">Leadership</span>
                </div>
              </div>

              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-info">
                    <div className="activity-icon-purple">
                      <IoMusicalNotes className="text-purple-600" />
                    </div>
                    <div>
                      <h3 className="activity-title">School Orchestra</h3>
                      <p className="activity-subtitle">First Violin • 4 years</p>
                    </div>
                  </div>
                  <button className="activity-menu-btn">
                    <IoEllipsisVertical />
                  </button>
                </div>
                <p className="activity-description">
                  Performed in state competitions and local concerts. Selected for All-State Orchestra two consecutive years.
                </p>
                <div className="activity-tags">
                  <span className="tag-purple">Arts</span>
                  <span className="tag-yellow">Awards</span>
                </div>
              </div>

              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-info">
                    <div className="activity-icon-red">
                      <IoNewspaper className="text-red-600" />
                    </div>
                    <div>
                      <h3 className="activity-title">School Newspaper</h3>
                      <p className="activity-subtitle">Editor-in-Chief • 2 years</p>
                    </div>
                  </div>
                  <button className="activity-menu-btn">
                    <IoEllipsisVertical />
                  </button>
                </div>
                <p className="activity-description">
                  Managed editorial team and published monthly issues. Won state journalism awards for investigative reporting.
                </p>
                <div className="activity-tags">
                  <span className="tag-blue">Leadership</span>
                  <span className="tag-red">Writing</span>
                </div>
              </div>
            </div>

            <div className="ai-review-banner">
              <IoBulb className="text-blue-600 text-xl" />
              <div className="banner-content">
                <h4 className="banner-title">AI Review Available</h4>
                <p className="banner-description">Get personalized feedback on your activities list from our AI counselor</p>
              </div>
              <button className="banner-btn">Review Now</button>
            </div>
          </div>

          {/* Essays Section */}
          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">
                Essays & Writing Supplements
                <InfoButton elementId="applications-essays" />
              </h2>
              <button className="section-action-btn">
                <IoAdd className="mr-1" />
                Start New Essay
              </button>
            </div>

            <div className="essays-list">
              <div className="essay-card">
                <div className="essay-header">
                  <div className="essay-content">
                    <div className="essay-title-row">
                      <h3 className="essay-title">Common App Personal Statement</h3>
                      <span className="essay-status-badge-green">Complete</span>
                    </div>
                    <p className="essay-prompt">
                      Prompt: Reflect on a time when you questioned or challenged a belief or idea...
                    </p>
                    <div className="essay-meta">
                      <span>
                        <IoDocumentText className="mr-1" />
                        648 words
                      </span>
                      <span>
                        <IoTime className="mr-1" />
                        Last edited: Dec 28, 2024
                      </span>
                      <span>
                        <IoEye className="mr-1" />
                        Reviewed by AI
                      </span>
                    </div>
                  </div>
                  <div className="essay-actions">
                    <button className="essay-action-btn-primary">
                      <IoCreate />
                    </button>
                    <button className="essay-action-btn-secondary">
                      <IoDownload />
                    </button>
                  </div>
                </div>
                <div className="essay-progress">
                  <div className="essay-progress-bar">
                    <div className="essay-progress-fill-green" style={{ width: '100%' }}></div>
                  </div>
                  <span className="essay-progress-text-green">100%</span>
                </div>
                <div className="essay-ai-feedback">
                  <IoConstruct className="text-green-600 mr-2" />
                  <span>
                    <strong>AI Score: 92/100</strong> - Excellent narrative structure and authentic voice. Minor grammar suggestions available.
                  </span>
                </div>
              </div>

              <div className="essay-card">
                <div className="essay-header">
                  <div className="essay-content">
                    <div className="essay-title-row">
                      <h3 className="essay-title">{currentCollege.essayTitle}</h3>
                      <span className="essay-status-badge-yellow">In Progress</span>
                    </div>
                    <p className="essay-prompt">
                      Prompt: What is the most significant challenge that society faces today?
                    </p>
                    <div className="essay-meta">
                      <span>
                        <IoDocumentText className="mr-1" />
                        412 words
                      </span>
                      <span>
                        <IoTime className="mr-1" />
                        Last edited: Jan 8, 2025
                      </span>
                      <span>
                        <IoTime className="mr-1" />
                        Draft
                      </span>
                    </div>
                  </div>
                  <div className="essay-actions">
                    <button className="essay-action-btn-primary">
                      <IoCreate />
                    </button>
                    <button className="essay-action-btn-secondary">
                      <IoDownload />
                    </button>
                  </div>
                </div>
                <div className="essay-progress">
                  <div className="essay-progress-bar">
                    <div className="essay-progress-fill-yellow" style={{ width: '65%' }}></div>
                  </div>
                  <span className="essay-progress-text-yellow">65%</span>
                </div>
                <div className="essay-actions-bottom">
                  <button className="essay-ai-btn">
                    <IoConstruct className="mr-2" />
                    Get AI Review
                  </button>
                  <button className="essay-continue-btn">Continue Writing</button>
                </div>
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="section-separator">
            <div className="separator-line"></div>
            <div className="separator-text">Documents & Recommendations</div>
            <div className="separator-line"></div>
          </div>

          {/* Documents & Recommendations Section */}
          <div className="documents-section">
            <div className="documents-grid">
              {/* Recommendations Card */}
              <div className="section-card">
                <div className="section-header">
                  <h2 className="section-title">Letters of Recommendation</h2>
                  <button className="section-action-btn">
                    <IoAdd className="mr-1" />
                    Request New
                  </button>
                </div>

                <div className="recommendations-list">
                  <div className="recommendation-item">
                    <div className="recommendation-header">
                      <div className="recommendation-info">
                        <img
                          alt="Teacher"
                          className="recommendation-avatar"
                          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                        />
                        <div>
                          <h3 className="recommendation-name">Dr. Robert Chen</h3>
                          <p className="recommendation-role">AP Chemistry Teacher</p>
                        </div>
                      </div>
                      <span className="recommendation-status-green">Submitted</span>
                    </div>
                    <div className="recommendation-meta">
                      <span>
                        <IoCalendar className="mr-1" />
                        Submitted: Dec 20, 2024
                      </span>
                      <span>
                        <IoSchool className="mr-1" />
                        For 5 schools
                      </span>
                    </div>
                  </div>

                  <div className="recommendation-item">
                    <div className="recommendation-header">
                      <div className="recommendation-info">
                        <img
                          alt="Teacher"
                          className="recommendation-avatar"
                          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg"
                        />
                        <div>
                          <h3 className="recommendation-name">Ms. Jennifer Martinez</h3>
                          <p className="recommendation-role">English Literature Teacher</p>
                        </div>
                      </div>
                      <span className="recommendation-status-yellow">Pending</span>
                    </div>
                    <div className="recommendation-meta">
                      <span>
                        <IoTime className="mr-1" />
                        Requested: Dec 15, 2024
                      </span>
                      <span>
                        <IoSchool className="mr-1" />
                        For 8 schools
                      </span>
                    </div>
                    <button className="reminder-btn">Send Reminder</button>
                  </div>

                  <div className="recommendation-item">
                    <div className="recommendation-header">
                      <div className="recommendation-info">
                        <img
                          alt="Counselor"
                          className="recommendation-avatar"
                          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg"
                        />
                        <div>
                          <h3 className="recommendation-name">Mr. David Thompson</h3>
                          <p className="recommendation-role">School Counselor</p>
                        </div>
                      </div>
                      <span className="recommendation-status-green">Submitted</span>
                    </div>
                    <div className="recommendation-meta">
                      <span>
                        <IoCalendar className="mr-1" />
                        Submitted: Dec 18, 2024
                      </span>
                      <span>
                        <IoSchool className="mr-1" />
                        For all schools
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Card */}
              <div className="section-card">
                <div className="section-header">
                  <h2 className="section-title">
                    Required Documents
                    <InfoButton elementId="applications-documents" />
                  </h2>
                  <button className="section-action-btn">
                    <IoAdd className="mr-1" />
                    Upload New
                  </button>
                </div>

                <div className="documents-list">
                  <div className="document-item-green">
                    <div className="document-info">
                      <div className="document-icon-green">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">Official Transcript</h3>
                        <p className="document-meta">Uploaded Dec 10, 2024 • 2.4 MB</p>
                      </div>
                    </div>
                    <IoCheckmarkCircle className="text-green-600 text-xl" />
                  </div>

                  <div className="document-item-green">
                    <div className="document-info">
                      <div className="document-icon-green">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">SAT Score Report</h3>
                        <p className="document-meta">Uploaded Dec 12, 2024 • 1.1 MB</p>
                      </div>
                    </div>
                    <IoCheckmarkCircle className="text-green-600 text-xl" />
                  </div>

                  <div className="document-item-green">
                    <div className="document-info">
                      <div className="document-icon-green">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">ACT Score Report</h3>
                        <p className="document-meta">Uploaded Dec 12, 2024 • 987 KB</p>
                      </div>
                    </div>
                    <IoCheckmarkCircle className="text-green-600 text-xl" />
                  </div>

                  <div className="document-item-yellow">
                    <div className="document-info">
                      <div className="document-icon-yellow">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">Mid-Year Report</h3>
                        <p className="document-meta">Required by Feb 1, 2025</p>
                      </div>
                    </div>
                    <button className="document-upload-btn">Upload</button>
                  </div>

                  <div className="document-item-gray">
                    <div className="document-info">
                      <div className="document-icon-gray">
                        <IoFileTray className="text-white" />
                      </div>
                      <div>
                        <h3 className="document-name">Financial Aid Documents</h3>
                        <p className="document-meta">Optional</p>
                      </div>
                    </div>
                    <button className="document-upload-btn-gray">Upload</button>
                  </div>
                </div>

                <div className="document-tips">
                  <IoInformationCircle className="text-blue-600 text-lg" />
                  <div>
                    <h4 className="tips-title">Document Tips</h4>
                    <ul className="tips-list">
                      <li>• All documents should be in PDF format</li>
                      <li>• Maximum file size: 5 MB per document</li>
                      <li>• Ensure all pages are clearly legible</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Applications;

